import { useState } from 'react';
import type { DiagramSortMode, Project, Diagram } from '../../store/types.ts';

type SidebarProps = {
  open: boolean;
  projects: Project[];
  diagrams: Diagram[];
  selectedProjectId: string;
  selectedDiagramId: string;
  diagramSearch: string;
  diagramSortMode: DiagramSortMode;
  onClose: () => void;
  onSelectProject: (id: string) => void;
  onCreateProject: () => void;
  onRenameProject: (id: string) => void;
  onDeleteProject: (id: string) => void;
  onSelectDiagram: (id: string) => void;
  onCreateDiagram: () => void;
  onRenameDiagram: (id: string) => void;
  onDeleteDiagram: (id: string) => void;
  onPinDiagram: (id: string) => void;
  onSearchChange: (query: string) => void;
  onSortChange: (mode: DiagramSortMode) => void;
};

export function Sidebar({
  open,
  projects,
  diagrams,
  selectedProjectId,
  selectedDiagramId,
  diagramSearch,
  diagramSortMode,
  onSelectProject,
  onCreateProject,
  onRenameProject,
  onDeleteProject,
  onSelectDiagram,
  onCreateDiagram,
  onRenameDiagram,
  onDeleteDiagram,
  onPinDiagram,
  onSearchChange,
  onSortChange,
}: SidebarProps) {
  const [projectsOpen, setProjectsOpen] = useState(true);
  const [diagramsOpen, setDiagramsOpen] = useState(true);

  return (
    <aside className={`ds-sidebar ${open ? '' : 'ds-sidebar--hidden'}`} aria-label="Project and diagram browser">
      {/* Projects */}
      <div className="ds-sidebar__section">
        <div className="ds-sidebar__section-head">
          <button
            type="button"
            className="ds-sidebar__section-toggle"
            onClick={() => setProjectsOpen((v) => !v)}
            aria-expanded={projectsOpen}
          >
            <span className={`ds-sidebar__chevron ${projectsOpen ? 'ds-sidebar__chevron--open' : ''}`}>&#x25B6;</span>
            <span className="ds-sidebar__section-title">Projects</span>
          </button>
          <button type="button" className="ds-sidebar__add-btn" onClick={onCreateProject} aria-label="New project">
            +
          </button>
        </div>
        {projectsOpen && projects.map((project) => (
          <div
            key={project.id}
            className={`ds-sidebar__item ${project.id === selectedProjectId ? 'ds-sidebar__item--active' : ''}`}
            onClick={() => onSelectProject(project.id)}
          >
            <div className="ds-sidebar__item-content">
              <span className="ds-sidebar__item-title">{project.name}</span>
              <span className="ds-sidebar__item-meta">Updated {new Date(project.updatedAt).toLocaleDateString()}</span>
            </div>
            <div className="ds-sidebar__item-actions">
              <button
                type="button"
                className="ds-sidebar__item-action"
                onClick={(e) => { e.stopPropagation(); onRenameProject(project.id); }}
                aria-label="Rename"
              >
                &#x270E;
              </button>
              <button
                type="button"
                className="ds-sidebar__item-action ds-sidebar__item-action--danger"
                onClick={(e) => { e.stopPropagation(); onDeleteProject(project.id); }}
                aria-label="Delete"
              >
                &#x2715;
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="ds-sidebar__divider" />

      {/* Diagrams */}
      <div className="ds-sidebar__section" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <div className="ds-sidebar__section-head">
          <button
            type="button"
            className="ds-sidebar__section-toggle"
            onClick={() => setDiagramsOpen((v) => !v)}
            aria-expanded={diagramsOpen}
          >
            <span className={`ds-sidebar__chevron ${diagramsOpen ? 'ds-sidebar__chevron--open' : ''}`}>&#x25B6;</span>
            <span className="ds-sidebar__section-title">Diagrams</span>
          </button>
          <button type="button" className="ds-sidebar__add-btn" onClick={onCreateDiagram} aria-label="New diagram">
            +
          </button>
        </div>

        {diagramsOpen && (
          <>
            <input
              className="ds-sidebar__search"
              value={diagramSearch}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search diagrams..."
              aria-label="Search diagrams"
            />

            <select
              className="ds-sidebar__sort"
              value={diagramSortMode}
              onChange={(e) => onSortChange(e.target.value as DiagramSortMode)}
              aria-label="Sort diagrams"
            >
              <option value="recent">Recent</option>
              <option value="oldest">Oldest</option>
              <option value="alpha">A - Z</option>
            </select>

            {diagrams.length === 0 && (
              <div className="ds-sidebar__empty">
                {diagramSearch ? 'No matching diagrams' : 'No diagrams yet'}
              </div>
            )}

            {diagrams.map((diagram) => (
              <div
                key={diagram.id}
                className={`ds-sidebar__item ${diagram.id === selectedDiagramId ? 'ds-sidebar__item--active' : ''}`}
                onClick={() => onSelectDiagram(diagram.id)}
              >
                <div className="ds-sidebar__item-content">
                  <span className="ds-sidebar__item-title">
                    {diagram.pinned && <span className="ds-sidebar__pin-badge" aria-label="Pinned" />}
                    {diagram.title}
                  </span>
                  <span className="ds-sidebar__item-meta">{new Date(diagram.updatedAt).toLocaleDateString()}</span>
                </div>
                <div className="ds-sidebar__item-actions">
                  <button
                    type="button"
                    className={`ds-sidebar__item-action ${diagram.pinned ? 'ds-sidebar__item-action--pinned' : ''}`}
                    onClick={(e) => { e.stopPropagation(); onPinDiagram(diagram.id); }}
                    aria-label={diagram.pinned ? 'Unpin' : 'Pin'}
                    title={diagram.pinned ? 'Unpin' : 'Pin to top'}
                  >
                    &#x1F4CC;
                  </button>
                  <button
                    type="button"
                    className="ds-sidebar__item-action"
                    onClick={(e) => { e.stopPropagation(); onRenameDiagram(diagram.id); }}
                    aria-label="Rename"
                  >
                    &#x270E;
                  </button>
                  <button
                    type="button"
                    className="ds-sidebar__item-action ds-sidebar__item-action--danger"
                    onClick={(e) => { e.stopPropagation(); onDeleteDiagram(diagram.id); }}
                    aria-label="Delete"
                  >
                    &#x2715;
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </aside>
  );
}
